import { Component, computed, inject, Signal, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { AbstractControl, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { EventService } from '../../../services/event.service';
import { ProjectService } from '../../../services/project.service';
import { ContactImportUpload, UploadService } from '../../../services/upload.service';
import { AdvancedSelectComponent } from '../../misc/advanced-select/advanced-select.component';
import { mapToNestedOptions } from '../../misc/nested-options';
import { SnackbarComponent } from '../../misc/snackbar/snackbar.component';

function xlsxValidator(control: AbstractControl) {
  const file = control.value as File | null;
  if (!file?.name) return null;

  const isXlsx =
    file.name.toLowerCase().endsWith('.xlsx') &&
    file.type ===
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

  return isXlsx ? null : { invalidFileType: true };
}


@Component({
  selector: 'app-upload',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatButtonModule,
    MatIconModule,
    AdvancedSelectComponent,
    MatChipsModule,
    MatInputModule,
    MatSnackBarModule
  ],
  templateUrl: './upload.component.html',
  styleUrl: './upload.component.scss',
})
export class UploadComponent {
  uploadForm: FormGroup;
  uploadFile?: File;
  readonly showAllOrAny: Signal<boolean> = signal(false);
  resetCounter = 0; // used to reset the filter form

  readonly projectService = inject(ProjectService);
  readonly eventService = inject(EventService);
  readonly uploadService = inject(UploadService);

  readonly _snackBar = inject(MatSnackBar);


  readonly allEvents = toSignal(this.eventService.getAllEvents(), { initialValue: [] });
  readonly allEventOptions = computed(() => mapToNestedOptions(this.allEvents(), true));
  readonly allProjects = toSignal(this.projectService.getAllProjects(), { initialValue: [] });
  readonly allProjectOptions = computed(() => mapToNestedOptions(this.allProjects(), true));

  constructor(private readonly fb: FormBuilder) {
    this.uploadForm = this.fb.group({
      importFile: [null],
      headerRows: [0, [
        Validators.required,
        Validators.min(0),
        Validators.max(10),
        Validators.pattern(/^\d+$/), // ensures integer
        xlsxValidator
      ]],
      batchName: [''],
      projects: { selectedValues: [], matchAll: false },
      events: { selectedValues: [], matchAll: false },
    })
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;

    if (input.files && input.files.length > 0) {
      this.uploadFile = input.files[0]; // get the first file
      console.log('Selected file:', this.uploadFile.name);
    } else {
      this.uploadFile = undefined; // no file selected
    }
  }

  clearFile() {
    this.uploadFile = undefined;
  }

  onSubmit() {
    const formValue = this.uploadForm.value;

    const uploadData: ContactImportUpload = {
      headerRows: formValue.headerRows,
      batchName: formValue.batchName,
      uploadFile: this.uploadFile,
      projects: formValue.projects?.selectedValues ?? [],
      events: formValue.events?.selectedValues ?? []
    }
    console.debug('submitting', uploadData);
    this.uploadService.upload(uploadData).subscribe({
      next: v => {
        console.debug('response', v);
        this._snackBar.openFromComponent(SnackbarComponent, {
          data: {
            message: `✅ File uploaded: imported/invalid: ${v.validCount}/${v.invalidCount}`,
            label: 'Process uploaded items',
            link: `/import/${v.id}`
          },
          duration: 0
        });
        this.uploadForm.reset({
          headerRows: 0
        });
        this.clearFile();
      },
      error: e => {
        console.error(e);
        this._snackBar.openFromComponent(SnackbarComponent, {
          data: {
            message: '❌ Error uploading file ' + e,
            link: undefined
          },
          duration: 0
        });
      }
    });
  }

}
